/**
 * Things the coach says over the lesson's own note because they matter more right now (ROUND7 §5): he is
 * bleeding, he is down, he is under water, a man has him in sight, or a dog has found him (ROUND8 §5). Each names
 * the key that deals with it, in his own bindings.
 */

import { HAZARDS } from '../../sim/tuning';
import { TICK_RATE, type World } from '../../sim/types';
import type { KeyNames } from './keys';

/** A live dog has him and is giving tongue for its handler. */
const dogHasHim = (w: World): boolean => w.enemies.some((e) => e.kind === 'dog' && e.state !== 'dead'
  && e.dog?.mode === 'bay');

export function interruption(w: World, keys: KeyNames): string | null {
  const r = w.ranger;
  if (w.insertion.phase !== 'ground') return null;
  if (r.status === 'downed') {
    return r.inventory.firstAid > 0
      ? `YOU ARE DOWN AND BLEEDING: ${keys.key('firstAid')} FOR FIRST AID, NOW. ANOTHER WOUND KILLS YOU.`
      : 'YOU ARE DOWN AND BLEEDING, WITH NO FIRST AID LEFT. IN THE FIELD, THIS IS HOW RANGERS ARE LOST.';
  }
  if (r.wounds > 0 && r.inventory.firstAid > 0 && r.action !== 'firstAid') {
    return `YOU ARE WOUNDED: HOLD STILL AND PRESS ${keys.key('firstAid')} FOR FIRST AID. THREE WOUNDS PUT YOU DOWN.`;
  }
  // Lying in water hides him from every eye (02 §6.1) - until his breath runs out (hazards/water.ts).
  if (w.hazards.submergedTicks > 0) {
    const stand = keys.crawlToggle ? `PRESS ${keys.key('crawl')}` : `LET GO OF ${keys.key('crawl')}`;
    return `YOU ARE UNDER WATER: NOBODY CAN SEE YOU, BUT YOUR BREATH LASTS `
      + `${Math.round(HAZARDS.drownAfterTicks / TICK_RATE)} SECONDS. ${stand} TO STAND UP.`;
  }
  if (dogHasHim(w)) {
    return 'THE DOG HAS YOU: ITS BARK BRINGS ITS HANDLER. GET INTO THE WATER - IT CANNOT FOLLOW YOU THROUGH IT.';
  }
  if (w.enemies.some((e) => e.state === 'combat' && e.seesRanger)) {
    return 'HE HAS SEEN YOU. THESE MEN ARE UNARMED - IN THE FIELD HE WOULD BE SHOOTING. GET OUT OF HIS SIGHT.';
  }
  return null;
}
